/**
 * ExecutionMonitor — Monitor de ejecución en tiempo real.
 *
 * Muestra hasta N runs concurrentes simultáneamente.
 * Cada run tiene su propia tarjeta con progreso de pasos.
 * Los eventos WS actualizan el store → re-render granular por run.
 *
 * Decisión de estado: useAppStore con selector por run_id.
 * Solo el componente del run afectado re-renderiza,
 * no toda la lista de runs concurrentes.
 */
import React, { useEffect, useCallback } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useAppStore } from '../../store';
import type { ExecutionRun, StepResult, RunStatus } from '../../types';
import { executions as executionsApi } from '../../services/api';

// ─── Status utilities ─────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<RunStatus, { color: string; bg: string; label: string; icon: string }> = {
  pending:  { color: '#5F5E5A', bg: '#F1EFE8', label: 'Pendiente', icon: '○' },
  running:  { color: '#185FA5', bg: '#E6F1FB', label: 'Ejecutando', icon: '◉' },
  passed:   { color: '#0F6E56', bg: '#E1F5EE', label: 'Pasó', icon: '✓' },
  failed:   { color: '#A32D2D', bg: '#FCEBEB', label: 'Falló', icon: '✗' },
  skipped:  { color: '#854F0B', bg: '#FAEEDA', label: 'Omitido', icon: '⊘' },
  error:    { color: '#793C1D', bg: '#FAECE7', label: 'Error', icon: '!' },
  cancelled:{ color: '#5F5E5A', bg: '#F1EFE8', label: 'Cancelado', icon: '⊘' },
};

function StatusBadge({ status, size = 'sm' }: { status: RunStatus; size?: 'sm' | 'lg' }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.pending;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: size === 'lg' ? '4px 12px' : '2px 8px',
      borderRadius: 6,
      background: cfg.bg, color: cfg.color,
      fontSize: size === 'lg' ? 12 : 10, fontWeight: 700,
    }}>
      <span style={{ fontSize: size === 'lg' ? 14 : 10 }}>{cfg.icon}</span>
      {cfg.label}
    </span>
  );
}

// ─── Progress Bar ─────────────────────────────────────────────────────────────

function ProgressBar({ passed, failed, total }: { passed: number; failed: number; total: number }) {
  const passedPct = total > 0 ? (passed / total) * 100 : 0;
  const failedPct = total > 0 ? (failed / total) * 100 : 0;
  const executedPct = passedPct + failedPct;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>
          {passed + failed} / {total} pasos
        </span>
        <span style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>
          {total > 0 ? Math.round(executedPct) : 0}%
        </span>
      </div>
      <div style={{
        height: 6, borderRadius: 3, background: 'var(--color-border-tertiary)',
        overflow: 'hidden', position: 'relative',
      }}>
        {/* Failed portion */}
        <div style={{
          position: 'absolute', right: 0, top: 0, bottom: 0,
          width: `${failedPct}%`, background: '#E24B4A',
          transition: 'width 0.4s',
        }} />
        {/* Passed portion */}
        <div style={{
          position: 'absolute', left: 0, top: 0, bottom: 0,
          width: `${passedPct}%`, background: '#1D9E75',
          transition: 'width 0.4s',
        }} />
        {/* Running pulse */}
        {executedPct < 100 && executedPct > 0 && (
          <div style={{
            position: 'absolute', left: `${executedPct}%`, top: 0, bottom: 0, width: 3,
            background: '#3B8BD4',
            animation: 'pulse 1s ease-in-out infinite',
          }} />
        )}
      </div>
    </div>
  );
}

// ─── Step Result Row ──────────────────────────────────────────────────────────

// Tipo extendido: StepResult + campos extra que llegan del agente via WS
interface LiveStepResult extends StepResult {
  description?: string;
  step_index?: number;
  action_id?: string;
}

function formatMs(ms: number) {
  return ms < 1000 ? `${Math.round(ms)}ms` : `${(ms / 1000).toFixed(1)}s`;
}

// Un paso puede tener varias acciones (p.ej. un SCREENSHOT agregado al final
// del paso) — cada una llega como su propio StepResult. Agrupamos por step_id
// para que el operador vea UNA fila por paso, no una por acción.
interface StepGroup {
  key: string;
  description?: string;
  status: RunStatus;
  duration_ms: number;
  actions: LiveStepResult[];
  hasHealing: boolean;
  error_summary?: string;
}

const STATUS_PRIORITY: RunStatus[] = ['error', 'failed', 'skipped', 'running', 'passed', 'pending'];

function groupStepResults(results: LiveStepResult[]): StepGroup[] {
  const order: string[] = [];
  const byKey = new Map<string, LiveStepResult[]>();
  results.forEach((r, i) => {
    const key = r.step_id ?? `idx-${i}`;
    if (!byKey.has(key)) { order.push(key); byKey.set(key, []); }
    byKey.get(key)!.push(r);
  });
  return order.map(key => {
    const actions = byKey.get(key)!;
    const status = STATUS_PRIORITY.find(s => actions.some(a => (a.status as RunStatus) === s)) ?? 'pending';
    const duration_ms = actions.reduce((sum, a) => sum + (a.duration_ms ?? 0), 0);
    const failedAction = actions.find(a => a.status === 'failed' || a.status === 'error');
    return {
      key,
      description: actions.find(a => a.description)?.description,
      status,
      duration_ms,
      actions,
      hasHealing: actions.some(a => Boolean(a.healing_applied)),
      error_summary: failedAction?.error_message,
    };
  });
}

// Icono compacto por acción individual dentro de un paso. Las acciones que
// capturaron pantalla se muestran como 📷 (clicable) en vez del ícono de estado.
function ActionDot({ result }: { result: LiveStepResult }) {
  const cfg = STATUS_CONFIG[result.status as RunStatus] ?? STATUS_CONFIG.pending;
  const isScreenshot = Boolean(result.screenshot_ref);
  const title = [cfg.label, result.duration_ms != null ? formatMs(result.duration_ms) : null]
    .filter(Boolean).join(' · ');

  const dot = (
    <span title={title} style={{
      width: 18, height: 18, borderRadius: 4,
      background: cfg.bg, color: cfg.color,
      fontSize: 9, fontWeight: 700,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0,
    }}>
      {isScreenshot ? '📷' : cfg.icon}
    </span>
  );

  return dot;
}

function StepGroupRow({ group, index }: { group: StepGroup; index: number }) {
  const cfg = STATUS_CONFIG[group.status] ?? STATUS_CONFIG.pending;

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 6,
      padding: '8px 12px', borderRadius: 8,
      background: 'var(--color-background-secondary)',
      border: `1px solid ${group.status === 'failed' || group.status === 'error' ? '#FCEBEB' : 'var(--color-border-tertiary)'}`,
      transition: 'all 0.3s',
      animation: 'slideIn 0.3s ease-out',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{
          width: 20, height: 20, borderRadius: 5,
          background: cfg.bg, color: cfg.color,
          fontSize: 10, fontWeight: 700,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          {cfg.icon}
        </span>

        <span style={{
          flex: 1, fontSize: 12, color: 'var(--color-text-primary)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {group.description ?? `Paso ${index + 1}`}
        </span>

        <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          {group.actions.map((a, i) => (
            <ActionDot key={a.action_id ?? a.id ?? i} result={a} />
          ))}
        </div>

        {group.duration_ms > 0 && (
          <span style={{ fontSize: 10, color: 'var(--color-text-tertiary)', whiteSpace: 'nowrap' }}>
            {formatMs(group.duration_ms)}
          </span>
        )}

        {group.hasHealing && (
          <span style={{
            padding: '1px 5px', borderRadius: 4,
            background: '#FAEEDA', color: '#854F0B',
            fontSize: 9, fontWeight: 700,
          }}>
            ✦
          </span>
        )}
      </div>

      {group.error_summary && (
        <div style={{ fontSize: 11, color: '#A32D2D', paddingLeft: 30 }}>
          {group.error_summary}
        </div>
      )}
    </div>
  );
}

// ─── Individual Run Card ──────────────────────────────────────────────────────

function RunCard({ run_id }: { run_id: string }) {
  // Granular selector — solo este run
  const run = useAppStore(state => state.activeRuns.get(run_id));

  if (!run) return null;

  const status = run.status as RunStatus;
  const isLive = status === 'running';

  const elapsed = run.started_at
    ? ((Date.now() - new Date(run.started_at).getTime()) / 1000).toFixed(0)
    : null;

  return (
    <div style={{
      borderRadius: 12,
      border: `1px solid ${isLive ? '#B5D4F4' : 'var(--color-border-tertiary)'}`,
      background: isLive ? '#E6F1FB08' : 'var(--color-background-primary)',
      overflow: 'hidden',
      transition: 'border-color 0.3s',
    }}>
      {/* Card header */}
      <div style={{
        padding: '14px 16px',
        display: 'flex', alignItems: 'center', gap: 12,
        borderBottom: '1px solid var(--color-border-tertiary)',
        background: isLive ? 'rgba(59,139,212,0.04)' : 'transparent',
      }}>
        <StatusBadge status={status} size="lg" />

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {run.case_title ?? run.case_id}
          </div>
          <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', marginTop: 2 }}>
            Run · {run_id.slice(0, 8)}
          </div>
        </div>

        {elapsed && isLive && (
          <div style={{
            fontSize: 11, color: '#185FA5', fontWeight: 600,
            fontVariantNumeric: 'tabular-nums',
            padding: '3px 8px', borderRadius: 6,
            background: '#E6F1FB',
          }}>
            ⏱ {elapsed}s
          </div>
        )}

        {run.duration_ms && !isLive && (
          <div style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>
            {(run.duration_ms / 1000).toFixed(1)}s
          </div>
        )}
      </div>

      {/* Progress */}
      <div style={{ padding: '12px 16px' }}>
        <ProgressBar
          passed={run.passed_steps}
          failed={run.failed_steps}
          total={run.total_steps}
        />

        {/* Step results — agrupados por paso, no por acción individual */}
        {run.step_results && run.step_results.length > 0 && (
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {groupStepResults(run.step_results as LiveStepResult[]).map((group, i) => (
              <StepGroupRow key={group.key} group={group} index={i} />
            ))}
            {/* Running indicator */}
            {isLive && (
              <div style={{
                padding: '8px 12px', borderRadius: 8,
                border: '1px dashed #B5D4F4',
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <div style={{
                  width: 8, height: 8, borderRadius: '50%',
                  background: '#3B8BD4',
                  animation: 'pulse 1s ease-in-out infinite',
                }} />
                <span style={{ fontSize: 11, color: '#185FA5' }}>Ejecutando próximo paso...</span>
              </div>
            )}
          </div>
        )}

        {/* Error message */}
        {run.error_summary && (
          <div style={{
            marginTop: 12, padding: 12, borderRadius: 8,
            background: '#FCEBEB', border: '1px solid #F7C1C1',
            fontSize: 12, color: '#791F1F',
          }}>
            <strong>Error:</strong> {run.error_summary}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Execution Monitor (main) ─────────────────────────────────────────────────

interface ExecutionMonitorProps {
  planId: string;
}

export function ExecutionMonitor({ planId }: ExecutionMonitorProps) {
  const { activeRuns, setPlans, startRun, addNotification } = useAppStore(useShallow(state => ({
    activeRuns: state.activeRuns,
    setPlans: state.setPlans,
    startRun: state.startRun,
    addNotification: state.addNotification,
  })));

  // Load plan and runs on mount
  useEffect(() => {
    const load = async () => {
      try {
        // Guard: skip fetch if planId is not a valid UUID
        const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        if (!UUID_RE.test(planId)) return;
        const runs = await executionsApi.getRuns(planId);
        runs.forEach(run => {
          if (!activeRuns.has(run.id)) {
            startRun(run);
          }
        });
      } catch {
        addNotification({ type: 'error', title: 'Error cargando runs' });
      }
    };
    load();
  }, [planId]);

  // activeRuns es un Map global de la sesión (acumula runs de cada plan que se
  // haya visitado, nunca se limpia). Sin este filtro por planId, el Monitor
  // mostraba también los runs de planes anteriores ya vistos.
  const planRuns = Array.from(activeRuns.values()).filter(r => r.plan_id === planId);
  const runIds = planRuns.map(r => r.id);
  const runningCount = planRuns.filter(r => r.status === 'running').length;
  const passedCount = planRuns.filter(r => r.status === 'passed').length;
  const failedCount = planRuns.filter(r => r.status === 'failed').length;

  return (
    <div>
      {/* Styles */}
      <style>{`
        @keyframes pulse { 0%,100% { opacity:1 } 50% { opacity:0.4 } }
        @keyframes slideIn { from { opacity:0; transform:translateY(-6px) } to { opacity:1; transform:translateY(0) } }
      `}</style>

      {/* Summary bar */}
      <div style={{
        display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap',
      }}>
        {[
          { label: 'Ejecutando', count: runningCount, color: '#185FA5', bg: '#E6F1FB' },
          { label: 'Pasaron', count: passedCount, color: '#0F6E56', bg: '#E1F5EE' },
          { label: 'Fallaron', count: failedCount, color: '#A32D2D', bg: '#FCEBEB' },
          { label: 'Total runs', count: runIds.length, color: '#5F5E5A', bg: '#F1EFE8' },
        ].map(item => (
          <div key={item.label} style={{
            padding: '10px 16px', borderRadius: 10,
            background: item.bg, color: item.color,
            display: 'flex', flexDirection: 'column', gap: 2,
            minWidth: 90,
          }}>
            <span style={{ fontSize: 24, fontWeight: 700, lineHeight: 1 }}>
              {item.count}
            </span>
            <span style={{ fontSize: 11, opacity: 0.8 }}>{item.label}</span>
          </div>
        ))}
      </div>

      {/* Run cards grid */}
      {runIds.length === 0 ? (
        <div style={{
          padding: '60px 0', textAlign: 'center',
          color: 'var(--color-text-tertiary)', fontSize: 14,
        }}>
          No hay ejecuciones activas. Inicia un plan para ver el progreso aquí.
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(460px, 1fr))',
          gap: 16,
        }}>
          {runIds.map(run_id => (
            <RunCard key={run_id} run_id={run_id} />
          ))}
        </div>
      )}
    </div>
  );
}
